// --- BASE ---
import React from 'react';

// --- COMPONENTS ---
import SelectCountry from '../../components/SelectCountry';
import SelectState from '../../components/SelectState';

// --- INTERFACES ---
import { IErrors, IRequest } from '../../Interfaces';

interface IProps {
	asset?: IRequest | null;
	disabled?: boolean;
	errors: IErrors;
	onChange: (name: string, value: string) => void;
}

// *** FUNCTIONAL COMPONENT ***
const CompanyInfo = (props: IProps) => {
	const { asset, disabled = false, errors, onChange }: IProps = { ...props };

	return (
		<fieldset className="mb-3">
			<legend>Company Information</legend>

			<p>Enter required company information to request an account.</p>

			<div className="row">
				<div className="col-md-12">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="company_name">
							Company Name
						</label>
						<input
							className={'form-control' + (errors?.company_name ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							id="company_name"
							maxLength={100}
							name="company_name"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="Company Name"
							type="text"
							value={asset?.company_name ?? ''}
						/>
						<div className="invalid-feedback">{errors?.company_name ?? ''}</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="first_name">
							First Name
						</label>
						<input
							className={'form-control' + (errors?.first_name ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							id="first_name"
							maxLength={100}
							name="first_name"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="First Name"
							type="text"
							value={asset?.first_name ?? ''}
						/>
						<div className="invalid-feedback">{errors?.first_name ?? ''}</div>
					</div>
				</div>

				<div className="col-md-6">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="last_name">
							Last Name
						</label>
						<input
							className={'form-control' + (errors?.last_name ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							id="last_name"
							maxLength={100}
							name="last_name"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="Last Name"
							type="text"
							value={asset?.last_name ?? ''}
						/>
						<div className="invalid-feedback">{errors?.last_name ?? ''}</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-md-6">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="country_code">
							Country
						</label>
						<SelectCountry
							className={'form-control' + (errors?.country_code ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							id="country_code"
							name="country_code"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							value={asset?.country_code ?? ''}
						/>
						<div className="invalid-feedback">{errors?.country_code ?? ''}</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-md-12">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="address">
							Address
						</label>

						<input
							className={'form-control' + (errors?.address1 ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							maxLength={60}
							name="address1"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="Address"
							type="text"
							value={asset?.address1 ?? ''}
						/>
						<input
							className={'form-control' + (errors?.address2 ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							maxLength={60}
							name="address2"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="Suite/Box"
							type="text"
							value={asset?.address2 ?? ''}
						/>
						<div className="invalid-feedback">{errors?.address1 ?? ''}</div>
					</div>
				</div>
			</div>

			<div className="row">
				<div className="col-md-4">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="city">
							City
						</label>
						<input
							className={'form-control' + (errors?.city ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							maxLength={30}
							name="city"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="City"
							type="text"
							value={asset?.city ?? ''}
						/>
						<div className="invalid-feedback">{errors?.city ?? ''}</div>
					</div>
				</div>

				<div className="col-md-4">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="state">
							State
						</label>
						<SelectState
							className={'form-control' + (errors?.state ?? false ? ' is-invalid' : '')}
							countryCode={asset?.country_code ?? ''}
							disabled={disabled}
							id="state"
							name="state"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							value={asset?.state ?? ''}
						/>
						<div className="invalid-feedback">{errors?.state ?? ''}</div>
					</div>
				</div>
				<div className="col-md-4">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="postal_code">
							Postal Code
						</label>
						<input
							className={'form-control' + (errors?.postal_code ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							maxLength={10}
							name="postal_code"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="Postal Code"
							type="text"
							value={asset?.postal_code ?? ''}
						/>
						<div className="invalid-feedback">{errors?.postal_code ?? ''}</div>
					</div>
				</div>

				<div className="col-md-4">
					<div className="mb-3">
						<label
							className="required"
							htmlFor="phone">
							Phone
						</label>
						<input
							className={'form-control' + (errors?.phone ?? false ? ' is-invalid' : '')}
							disabled={disabled}
							maxLength={30}
							name="phone"
							onChange={(e) => onChange(e.currentTarget.name, e.target.value)}
							placeholder="Phone"
							type="tel"
							value={asset?.phone ?? ''}
						/>
						<div className="invalid-feedback">{errors?.phone ?? ''}</div>
					</div>
				</div>
			</div>
		</fieldset>
	);
};

export default CompanyInfo;
